import type { Processor, Test, TestMapping } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged, PaginationParams } from "../_shared/pagination.types";
import { TESTS_ENDPOINTS } from "./tests.consts";
import type {
  TestsByInstrumentParams,
  TestsFilterParams,
  TestCreateRequest,
  TestUpdateRequest,
  TestMappingCreateRequest,
  TestMappingUpdateRequest,
  ProcessorCreateRequest,
  ProcessorUpdateRequest,
} from "./tests.types";

/**
 * Tests API Client
 * All endpoints are nested under /instruments/{instrumentId}/tests/...
 */
export class TestsClient extends BaseClient {
  /**
   * Fetch paginated tests for an instrument.
   */
  async fetchTestsByInstrument(
    instrumentId: number,
    params?: TestsByInstrumentParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<Test>> {
    return this.get<Paged<Test>>(TESTS_ENDPOINTS.TESTS_BY_INSTRUMENT(instrumentId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch filtered tests for an instrument (paged). Callers that need the
   * complete filtered set (e.g. duplicate-check lookups) should walk the pages.
   */
  async fetchTestsFilter(
    instrumentId: number,
    params?: TestsFilterParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<Test>> {
    return this.get<Paged<Test>>(TESTS_ENDPOINTS.TESTS_FILTER(instrumentId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  /**
   * Fetch a single test by ID.
   */
  async fetchTestById(instrumentId: number, id: number, signal?: AbortSignal | null): Promise<Test> {
    return this.get<Test>(TESTS_ENDPOINTS.TEST_BY_ID(instrumentId, id), { signal });
  }

  /**
   * Create a new test. instrumentId is derived from data.instrumentId.
   */
  async createTest(data: TestCreateRequest, signal?: AbortSignal | null): Promise<Test> {
    return this.post<Test>(TESTS_ENDPOINTS.CREATE_TEST(data.instrumentId), { body: data, signal });
  }

  /**
   * Update an existing test. instrumentId is derived from data.instrumentId.
   */
  async updateTest(id: number, data: TestUpdateRequest, signal?: AbortSignal | null): Promise<Test> {
    return this.put<Test>(TESTS_ENDPOINTS.UPDATE_TEST(data.instrumentId, id), { body: data, signal });
  }

  /**
   * Delete a test.
   */
  async deleteTest(instrumentId: number, id: number, signal?: AbortSignal | null): Promise<void> {
    return this.delete(TESTS_ENDPOINTS.DELETE_TEST(instrumentId, id), { signal });
  }

  // ---------------------------------------------------------------------------
  // Mappings
  // ---------------------------------------------------------------------------

  async fetchMappings(
    instrumentId: number,
    testId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<TestMapping>> {
    return this.get<Paged<TestMapping>>(TESTS_ENDPOINTS.TEST_MAPPINGS(instrumentId, testId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  async createMapping(
    instrumentId: number,
    testId: number,
    data: TestMappingCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<TestMapping> {
    return this.post<TestMapping>(TESTS_ENDPOINTS.TEST_MAPPINGS(instrumentId, testId), { body: data, signal });
  }

  async updateMapping(
    instrumentId: number,
    testId: number,
    mappingKey: string,
    data: TestMappingUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<TestMapping> {
    return this.put<TestMapping>(TESTS_ENDPOINTS.TEST_MAPPING_BY_KEY(instrumentId, testId, mappingKey), {
      body: data,
      signal,
    });
  }

  async deleteMapping(
    instrumentId: number,
    testId: number,
    mappingKey: string,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.delete(TESTS_ENDPOINTS.TEST_MAPPING_BY_KEY(instrumentId, testId, mappingKey), { signal });
  }

  // ---------------------------------------------------------------------------
  // Processors
  // ---------------------------------------------------------------------------

  async fetchProcessors(
    instrumentId: number,
    testId: number,
    params?: PaginationParams,
    signal?: AbortSignal | null,
  ): Promise<Paged<Processor>> {
    return this.get<Paged<Processor>>(TESTS_ENDPOINTS.TEST_PROCESSORS(instrumentId, testId), {
      params: params as Record<string, unknown>,
      signal,
    });
  }

  async createProcessor(
    instrumentId: number,
    testId: number,
    data: ProcessorCreateRequest,
    signal?: AbortSignal | null,
  ): Promise<Processor> {
    return this.post<Processor>(TESTS_ENDPOINTS.TEST_PROCESSORS(instrumentId, testId), { body: data, signal });
  }

  async updateProcessor(
    instrumentId: number,
    testId: number,
    processorId: number,
    data: ProcessorUpdateRequest,
    signal?: AbortSignal | null,
  ): Promise<Processor> {
    return this.put<Processor>(TESTS_ENDPOINTS.TEST_PROCESSOR_BY_ID(instrumentId, testId, processorId), {
      body: data,
      signal,
    });
  }

  async deleteProcessor(
    instrumentId: number,
    testId: number,
    processorId: number,
    signal?: AbortSignal | null,
  ): Promise<void> {
    return this.delete(TESTS_ENDPOINTS.TEST_PROCESSOR_BY_ID(instrumentId, testId, processorId), { signal });
  }
}
